import React, { useState, useRef, useEffect } from "react";
import './GetStarted.css';
import rightarrow from '../../Images/get-started-right-arrow.svg';
import emailjs from '@emailjs/browser';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactPhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const MAX_LINES = 5;
const LINE_HEIGHT = 24; 

const GetStarted = () => {
    const [formData, setFormData] = useState({ name: '', emailaddress: '', phonenumber: '+971', message: '' });
    const [isFormValid, setIsFormValid] = useState(false);
    const textareaRef = useRef(null);
    const phoneInputRef = useRef(null);
    const [formErrors, setFormErrors] = useState({ name: true, emailaddress: true, message: true });

    const countVisualLines = (text) => {
        const textarea = textareaRef.current;
        if (!textarea) return 0;
        const temp = document.createElement('textarea');
        temp.style.visibility = 'hidden';
        temp.style.position = 'absolute';
        const style = window.getComputedStyle(textarea);
        temp.style.width = style.width;
        temp.style.font = style.font;
        temp.style.letterSpacing = style.letterSpacing;
        temp.style.padding = style.padding;
        temp.style.border = style.border;
        temp.style.boxSizing = style.boxSizing;
        temp.value = text;
        document.body.appendChild(temp);
        const totalHeight = temp.scrollHeight;
        const paddingTop = parseFloat(style.paddingTop);
        const paddingBottom = parseFloat(style.paddingBottom);
        const textHeight = totalHeight - paddingTop - paddingBottom;
        document.body.removeChild(temp);
        const lines = Math.ceil(textHeight / LINE_HEIGHT);
        return Math.max(1, lines);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'name' || name === 'emailaddress') {
            let cleanedValue = value;
            if (name === 'name') {
                cleanedValue = value.replace(/[^A-Za-z\s]/g, '');
            }
            setFormData(prev => ({ ...prev, [name]: cleanedValue }));
            let isValid = false;
            if (name === 'name') {
                isValid = cleanedValue.trim().length > 0;
            } else if (name === 'emailaddress') {
                isValid = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(cleanedValue);
            }
            setFormErrors(prev => ({ ...prev, [name]: !isValid }));
            return;
        }

        if (name === 'message') {
            const newLines = countVisualLines(value);
            if (newLines <= MAX_LINES || value.length < formData.message.length) {
                setFormData(prev => ({ ...prev, message: value }));
                setFormErrors(prev => ({ ...prev, message: value.trim().length === 0 }));
            } else {
                toast.warn(`You can only write up to ${MAX_LINES} lines.`, {
                    position: 'top-center',
                    autoClose: 2000,
                });
            }
        }
    };
    
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
        }
    }, [formData.message]);


    const handleMessagePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text');
        const currentMessage = formData.message;
        const start = e.target.selectionStart;
        const end = e.target.selectionEnd;
        const newMessage = currentMessage.substring(0, start) + pastedData + currentMessage.substring(end);

        if (countVisualLines(newMessage) <= MAX_LINES) {
            setFormData(prev => ({ ...prev, message: newMessage }));
            setFormErrors(prev => ({ ...prev, message: newMessage.trim().length === 0 }));
        } else {
            toast.warn(`Pasting this text would exceed the ${MAX_LINES}-line limit.`, {
                position: 'top-center',
                autoClose: 2000,
            });
        }
    };
    
    const handleMessageKeyDown = (e) => {
        if (e.key === 'Enter' && countVisualLines(formData.message) >= MAX_LINES) {
            e.preventDefault();
        }
    };

    useEffect(() => {
        const isAllValid =
            !formErrors.name &&
            !formErrors.emailaddress &&
            !formErrors.message &&
            formData.name.trim() !== '' &&
            formData.emailaddress.trim() !== '' &&
            formData.message.trim() !== '';

        setIsFormValid(isAllValid);
    }, [formErrors, formData]); 


    const sendEmail = (e) => {
        e.preventDefault();
        emailjs.send('service_8mh27ro', 'template_71gumek', formData, '1vfa7JzSXwfglqiJK')
            .then(() => {
                toast.success('Message sent successfully!', { position: 'top-center', autoClose: 2000 });
                setFormData({ name: '', emailaddress: '', phonenumber: '+971', message: '' });
            })
            .catch((error) => {
                console.error('Error:', error);
                toast.error('Failed to send message.', { position: 'top-center', autoClose: 2000 });
            });
    };

    const handlePhoneChange = (value, country) => {
        if (!value.startsWith(country.dialCode)) return;
        setFormData(prev => ({ ...prev, phonenumber: value }));
    };

    const handlePhoneKeyDown = (e) => {
        const input = phoneInputRef.current;
        if (!input) return;
        const dialCode = input.value.match(/^\+\d+/)?.[0] || '';
        const dialCodeLength = dialCode.length;
        const cursorPos = input.selectionStart;
        if ((e.key === 'Backspace' && cursorPos <= dialCodeLength) ||
            (e.key === 'Delete' && cursorPos < dialCodeLength)) {
            e.preventDefault();
        }
    };

    return (
        <>
            <div className="get-started-section" id='getstarted'>
                <div className="get-started-heading">
                    <h2 className="get-started-title">Get Started</h2>
                    <h2 className="get-started-subtitle">Get in touch with us. We're here to <br />assist you.</h2>
                </div>
                <div className="form-container">
                    <form className="contact-form" onSubmit={sendEmail}>
                        <div className="input-row">
                            <input type='text' name='name' placeholder="Your name" className="contact-input" value={formData.name} onChange={handleChange} autoComplete="off" />
                            <input type='email' name='emailaddress' placeholder="Email Address" className="contact-input" value={formData.emailaddress} onChange={handleChange} autoComplete="off" />
                            <div className="phone-input-wrapper">
                                <ReactPhoneInput
                                    inputClass="phone-inner-input" buttonClass="phone-button" containerClass="phone-container"
                                    dropdownClass="phone-dropdown" country={'ae'} value={formData.phonenumber}
                                    onChange={handlePhoneChange}
                                    inputProps={{ name: 'phonenumber', ref: phoneInputRef, onKeyDown: handlePhoneKeyDown, autoComplete: 'off' }}
                                    inputStyle={{ background: 'transparent', color: '#fff', border: 'none', borderBottom: '1px solid #999', borderRadius: '0', width: '100%' }}
                                    buttonStyle={{ background: 'transparent', border: 'none' }}
                                />
                            </div>
                        </div>
                        <textarea
                            className='contact-textarea' ref={textareaRef} name='message' placeholder="Message"
                            value={formData.message} onChange={handleChange} onKeyDown={handleMessageKeyDown} onPaste={handleMessagePaste}
                            autoComplete="off"
                        ></textarea>
                        <button type="submit" className="leave-message-btn" disabled={!isFormValid}>Leave us a Message <span className="message-right-arrow"><img src={rightarrow} alt='right arrow' className="right arrow" /></span></button>
                    </form>
                </div>
            </div>
            <ToastContainer />
        </>
    );
}

export default GetStarted;